import { useCallback, useRef, useState } from 'react';
import billingClient from '~/modules/common/utils/billing-client/client-v2';

export type MemberFederationStatus =
  | 'loading'
  | 'federated'
  | 'unfederated'
  | 'error';

export interface MemberFederation {
  statusFor: (userId: string) => MemberFederationStatus | undefined;
  load: (userId: string) => void;
}

/**
 * Looks up, on demand, whether enterprise members' emails are owned by their
 * identity provider, remembering each answer for the component's lifetime.
 */
const useMemberFederation = (
  token: string,
  enterpriseId: string
): MemberFederation => {
  const [statuses, setStatuses] = useState<
    Record<string, MemberFederationStatus>
  >({});
  const requested = useRef(new Set<string>());

  const setStatus = (userId: string, status: MemberFederationStatus) =>
    setStatuses(previous => ({ ...previous, [userId]: status }));

  const load = useCallback(
    async (userId: string) => {
      if (requested.current.has(userId)) {
        return;
      }
      requested.current.add(userId);
      setStatus(userId, 'loading');

      try {
        const { federated } =
          await billingClient.enterpriseMembers.getFederation({
            token,
            enterpriseId,
            memberId: userId
          });
        setStatus(userId, federated ? 'federated' : 'unfederated');
      } catch {
        requested.current.delete(userId);
        setStatus(userId, 'error');
      }
    },
    [token, enterpriseId]
  );

  const statusFor = useCallback(
    (userId: string) => statuses[userId],
    [statuses]
  );

  return { statusFor, load };
};

export default useMemberFederation;
